import { Plugin } from '@uppy/core';
import Translator from '@uppy/utils/lib/Translator';
import _ from 'lodash';
import { CancelToken } from 'axios';
import { timer } from '@utilities/helpers'; // eslint-disable-line
import VideoTranscoderService from '../services/VideoTranscoderService';

class UppyDigitalOceanPlugin extends Plugin {
  constructor(uppy, opts = {}) {
    super(uppy, opts);
    this.id = opts.id || 'uppy-vimeo-plugin';
    this.teacherId = opts.teacherId;
    this.studentId = opts.studentId;
    this.pollingInterval = opts.pollingInterval || 30000; // 30 seconds
    this.pollingTimeout = opts.pollingTimeout || 3600000; // 1 hour
    this.type = 'modifier';
    this.defaultLocale = {
      strings: {
        initializingPlaypositVideo: 'Initializing PlayPosit video...',
        updatingPlaypositVideo: 'Updating PlayPosit video...',
        initializingTranscodingServices: 'Initializing transcoding services...',
        queuedForTranscoding: 'Queued for transcoding',
        transcoding: 'Transcoding...',
        finalizingVideo: 'Finalizing video...',
      },
    };

    this.i18nInit();
    this.prepareUpload = this.prepareUpload.bind(this);
    this.handleUpload = this.handleUpload.bind(this);
    this.handleUploadCompletion = this.handleUploadCompletion.bind(this);
    this.handleCancellation = this.handleCancellation.bind(this);
    this.poll = this.poll.bind(this);
  }

  setOptions(newOpts) {
    super.setOptions(newOpts);
    this.i18nInit();
  }

  i18nInit() {
    this.translator = new Translator([
      this.defaultLocale,
      this.uppy.locale,
      this.opts.locale,
    ]);
    this.i18n = this.translator.translate.bind(this.translator);
    this.setPluginState();
  }

  generateCancelToken() {
    this.cancelTokenSource = CancelToken.source();
  }

  cancelRequests() {
    if (_.isFunction(this?.cancelTokenSource?.cancel)) this.cancelTokenSource.cancel();
  }

  async handleCancellation(removedFile) {
    this.cancelRequests();
    let unfinishedFiles = [];
    if (removedFile) {
      unfinishedFiles = [removedFile];
    } else {
      unfinishedFiles = (this.uppy.getFiles() || []).filter((file) => (
        !file
        || !file.isFinished
      ));
    }
    if (!unfinishedFiles.length) return;
    await Promise.all(unfinishedFiles.map((file) => {
      const transcodingServiceStatuses = file.transcodingServiceStatusColumns.reduce((statuses, value) => {
        statuses[value] = 'canceled';
        return statuses;
      }, {});
      return VideoTranscoderService.updateHostingLog({
        id: file?.hostingLog?.id,
        ...!file?.hasFileFinishedUploading && { originalFileStatus: 'canceled' },
        ...!file?.hasFileFinishedUploading && transcodingServiceStatuses,
      });
    }));
  }

  async prepareUpload(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        const file = this.uppy.getFile(fileId);
        if (file.type.split('/')[0] !== 'video') return false;
        this.uppy.emit('preprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('initializingPlaypositVideo'),
        });
        this.generateCancelToken();
        const {
          data: {
            digitalOcean,
            hostingLog,
            video: playpositVideo,
            transcodingServiceStatusColumns,
          },
        } = await VideoTranscoderService.initializeDigitalOceanUpload({
          name: file.name,
          contentType: file.type,
          teacherId: this.teacherId,
          studentId: this.studentId,
        }, {
          cancelToken: this.cancelTokenSource.token,
        });
        this.uppy.setFileState(fileId, {
          digitalOcean,
          duration: file.duration,
          hostingLog,
          playpositVideo,
          transcodingServiceStatusColumns,
        });
        return file;
      } catch(e) {
        this.uppy.log('[Uppy PlayPosit DigitalOcean] Failed to initialize a video: ', 'warning');
        this.uppy.log(e, 'warning');
        return false;
      }
    });
    const emitPreprocessCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('preprocess-complete', file);
      }
    };
    return Promise.all(promises)
      .then(emitPreprocessCompleteForAll);
  }

  async handleUpload(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('upload-started', file);
        const response = await VideoTranscoderService.uploadFileToDigitalOcean({
          file,
          cancelToken: this.cancelTokenSource.token,
          onUploadProgress: ({
            loaded,
            total,
          } = {}) => {
            this.uppy.emit('upload-progress', file, {
              bytesUploaded: loaded,
              bytesTotal: total,
            });
          },
        });
        this.uppy.setFileState(fileId, {
          originalFileUrl: _.get(file, 'digitalOcean.url', '').split('?')[0],
          uploadResponse: {
            status: response.status,
            body: response.data,
            uploadURL: _.get(file, 'digitalOcean.url', ''),
          },
        });
        return file;
      } catch(e) {
        this.uppy.log('[Uppy PlayPosit DigitalOcean] Failed to initialize a video: ', 'warning');
        this.uppy.log(e, 'warning');
        return false;
      }
    });
    const emitUploadCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('upload-success', file, file.uploadResponse);
      }
    };
    return Promise.all(promises)
      .then(emitUploadCompleteForAll);
  }

  poll({
    pollingFunction,
    file,
  } = {}) {
    const endTime = Number(new Date()) + this.pollingTimeout;
    let timeoutId;

    function clearPollingTimeout() {
      clearTimeout(timeoutId);
    }

    this.uppy.off('file-removed', clearPollingTimeout);
    this.uppy.off('cancel-all', clearPollingTimeout);
    this.uppy.on('file-removed', clearPollingTimeout);
    this.uppy.on('cancel-all', clearPollingTimeout);

    const checkCondition = async (resolve, reject) => {
      try {
        const response = await pollingFunction();
        if (_.get(response, 'data.transcodingProgress')) {
          this.uppy.emit('postprocess-progress', file, {
            mode: 'determinate',
            message: this.i18n('transcoding'),
            value: parseFloat(response.data.transcodingProgress) / 100,
          });
        }
        if (_.get(response, 'data.isVideoReady')) {
          this.uppy.emit('postprocess-progress', file, {
            mode: 'determinate',
            message: this.i18n('transcoding'),
            value: 1,
          });
          resolve(response.data);
        } else if (Number(new Date()) < endTime) {
          timeoutId = setTimeout(checkCondition, this.pollingInterval, resolve, reject);
        } else {
          reject(new Error('Timed out.'));
        }
      } catch(e) {
        if (_.get(e, 'response.status') === 404) {
          timeoutId = setTimeout(checkCondition, this.pollingInterval, resolve, reject);
        } else {
          clearPollingTimeout();
          reject(e);
        }
      }
    };

    return new Promise(checkCondition);
  }

  async handleUploadCompletion(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        this.uppy.setFileState(fileId, {
          hasFileFinishedUploading: true,
        });
        const file = this.uppy.getFile(fileId);
        await Promise.all([
          VideoTranscoderService.updateHostingLog({
            id: file.hostingLog.id,
            originalFileStatus: 'ready',
          }, { cancelToken: this.cancelTokenSource.token }),
          VideoTranscoderService.createTeacherVideo({
            name: file.name,
            videoId: file.hostingLog.videoId,
            teacherId: this.teacherId,
          }, { cancelToken: this.cancelTokenSource.token }),
        ]);
        this.uppy.emit('teacher-video-created');
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('initializingTranscodingServices'),
          value: 0,
        });
        await VideoTranscoderService.initializeTranscodeServices(file.hostingLog.id, { cancelToken: this.cancelTokenSource.token });
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('queuedForTranscoding'),
          value: 0,
        });
        const { video } = await this.poll({
          pollingFunction: () => VideoTranscoderService.verifyTranscodeServices(file.hostingLog.id, { cancelToken: this.cancelTokenSource.token }),
          file,
        });
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('finalizingVideo'),
          value: 0,
        });
        await Promise.all([
          VideoTranscoderService.updateHostingLog({
            id: file.hostingLog.id,
            transcodingProgress: 100,
          }, { cancelToken: this.cancelTokenSource.token }),
          timer(10000),
        ]);
        this.uppy.setFileState(fileId, {
          playpositVideo: video,
          duration: video.playerDuration,
          isFinished: true,
        });
        return this.uppy.getFile(fileId);
      } catch(e) {
        this.uppy.log('[Uppy PlayPosit Vimeo] Failed to update a video:', 'warning');
        this.uppy.log(e, 'warning');
        this.uppy.cancelAll();
        this.uppy.emit('error', e);
        throw e;
      }
    });

    const emitPostprocessCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        if (file) this.uppy.emit('postprocess-complete', file);
      }
    };

    return Promise.all(promises)
      .then(emitPostprocessCompleteForAll);
  }

  install() {
    this.uppy.addPreProcessor(this.prepareUpload);
    this.uppy.addUploader(this.handleUpload);
    this.uppy.addPostProcessor(this.handleUploadCompletion);
    this.uppy.on('cancel-all', this.handleCancellation);
    this.uppy.on('file-removed', this.handleCancellation);
  }

  uninstall() {
    this.uppy.removePreProcessor(this.prepareUpload);
    this.uppy.removePostProcessor(this.handleUploadCompletion);
    this.uppy.off('cancel-all', this.handleCancellation);
    this.uppy.off('file-removed', this.handleCancellation);
  }
}

export default UppyDigitalOceanPlugin;
